import { OpenInNew } from "@mui/icons-material";
import { Button } from "@mui/material";
import { DataType, SortDirection } from "ka-table";
import { Column } from "ka-table/models";
import { ICellProps } from "ka-table/props";
import { ChildAttributesItem } from "ka-table/types";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import CrmTable from "../App/Components/Table/CrmTable";
import { useImportChanges } from "../App/Queries/ImportQueries";
import { useDebouncedHandler } from "../App/State/Hooks/useDebouncedHandler";
import { useImportTabStore } from "../App/State/Stores/ImportTabStore";
import { IImportChange } from "../App/Typings/Imports";
import {
  ImportSearchBar,
  MaxSearchLength,
  paginationOptions,
  renderTablePaginationDisplay,
} from "./Components/SharedTableElements";
import { getChangeSourceDisplay } from "./Imports";

const columns: Column[] = [
  {
    title: "Row Number",
    key: "rowNumber",
    dataType: DataType.Number,
    sortDirection: SortDirection.Ascend,
    width: 140,
  },
  { title: "Date Added", key: "dateChanged", dataType: DataType.Date },
  { title: "Name", key: "name", dataType: DataType.String },
  { title: "Import Type", key: "changeSource", dataType: DataType.String },
  {
    title: "Profile",
    key: "candidateId",
    dataType: DataType.String,
    isEditable: false,
    isSortable: false,
    isFilterable: false,
  },
];

export function ImportTable({ statusId }: { statusId: string }) {
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputError, setSearchInputError] = useState<string>("");

  const {
    query: { search, sortBy, sortDirection, page, pageSize },
    setPage,
    setSearch,
    handleRequestSort,
    handleChangePage,
    handleChangePageSize,
    toQueryString,
  } = useImportTabStore(
    useShallow((state) => ({
      query: state.query,
      setPage: state.setPage,
      setSearch: state.setSearch,
      handleRequestSort: state.handleRequestSort,
      handleChangePage: state.handleChangePage,
      handleChangePageSize: state.handleChangePageSize,
      toQueryString: state.toQueryString,
    })),
  );

  const [queryString, setQueryString] = useState<string>("");
  const { data: importChanges, isLoading } = useImportChanges(statusId, queryString);

  const itemCount = importChanges?.items ? importChanges.items.length : 0;
  const totalCount = importChanges?.totalItems ?? 0;
  const totalCountDisplay = totalCount > pageSize ? totalCount : itemCount;

  const handleChangeSortBy = (sortBy: string) => {
    let sortProperty = "rowNumber";
    switch (sortBy) {
      case "changeSource":
        sortProperty = "type";
        break;
      case "rowNumber":
      case "dateChanged":
      case "name":
        sortProperty = sortBy;
    }
    handleRequestSort(sortProperty);
  };

  const handleSearchFilter = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > MaxSearchLength) {
      setSearchInputError(`Search input must be less than or equal to ${MaxSearchLength} characters`);
    } else {
      setSearchInputError("");
    }
    setSearchInput(e.target.value);
  }, 300);

  useEffect(() => {
    if (searchInput.length <= MaxSearchLength && searchInput !== search) {
      setSearch(searchInput);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search]);

  useEffect(() => {
    setQueryString(toQueryString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortDirection, search]);

  return (
    <>
      <ImportSearchBar
        label="Filter Imports"
        placeholder="Search by row number, date added or name"
        defaultValue={search}
        error={searchInputError.length > 0}
        errorMessage={searchInputError}
        dataTestId="import-conflicts-searchbar"
        onChange={handleSearchFilter}
      />
      <CrmTable
        name="import-changes"
        columns={columns}
        loading={isLoading}
        data={importChanges?.items ?? []}
        pagination={{
          ...paginationOptions,
          currentPage: page,
          rows: pageSize,
          handleChangePage: handleChangePage,
          handleChangeRowsPerPage: handleChangePageSize,
          totalCount: totalCountDisplay,
          labelDisplayedRows: ({ from, to, count }) =>
            renderTablePaginationDisplay({
              from,
              to,
              count,
              isLoading,
              search,
              itemName: "imports",
            }),
        }}
        order={sortDirection}
        orderBy={sortBy}
        onSort={handleChangeSortBy}
        rowTestId={(row: IImportChange) => `row-${row.rowNumber}`}
        customCell={{
          elementAttributes: (props) =>
            ({
              "data-test": `item-${props.column.key}-${props.rowData.id}`,
            }) as ChildAttributesItem<ICellProps<IImportChange>>,
          content: (props) => {
            if (props.column.key === "changeSource") {
              return getChangeSourceDisplay(props.rowData.changeSource);
            }
            if (props.column.key === "candidateId") {
              return (
                <Button
                  component={Link}
                  endIcon={<OpenInNew fontSize="small" />}
                  to={`/candidates/${props.rowData.candidateId}`}
                  target="_blank"
                >
                  Link to Candidate
                </Button>
              );
            }
          },
        }}
      />
    </>
  );
}
